/**
 * Palette de commandes (⌘K / Ctrl+K) — navigation rapide vers toutes les
 * routes du registre autorisées pour l'utilisateur (rôle + features tenant),
 * et recherche d'entités (transporteurs) dès que l'utilisateur tape.
 * Implémentation maison sans dépendance : overlay + focus trap partagé
 * (useDialogA11y), filtrage insensible aux accents, navigation aux flèches.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CornerDownLeft, Search, Truck } from 'lucide-react';
import { FEATURE_DEFAULTS, type ICarrier } from '@carrierbridge/shared';
import { cn } from '@/lib/utils';
import { useAuthContext } from '@/contexts/AuthContext';
import { navigableRoutes, type RouteEntry } from '@/lib/route-registry';
import { carriersListQueryOptions } from '@/hooks/useCarriers';
import { useDialogA11y } from '@/components/ui/useDialogA11y';

function normalize(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

const MAX_ENTITY_RESULTS = 8;

type PaletteItem =
  | { kind: 'route'; entry: RouteEntry }
  | { kind: 'carrier'; carrier: ICarrier };

function itemKey(item: PaletteItem): string {
  return item.kind === 'route' ? item.entry.path : `carrier:${item.carrier.id}`;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useDialogA11y(open, panelRef);

  const routes = useMemo(() => navigableRoutes(user, FEATURE_DEFAULTS), [user]);

  // Recherche d'entités : mêmes règles d'accès que la page /carriers.
  const canSearchCarriers = ['admin', 'editor', 'superadmin'].includes(user.role);
  const { data: carriers } = useQuery({
    ...carriersListQueryOptions('ALL'),
    enabled: open && canSearchCarriers,
    staleTime: 60_000,
  });

  const results = useMemo<PaletteItem[]>(() => {
    const q = normalize(query.trim());
    const routeItems: PaletteItem[] = (q
      ? routes.filter((r) =>
          normalize(`${t(r.labelKey)} ${r.keywordsKey ? t(r.keywordsKey) : ''}`).includes(q),
        )
      : routes
    ).map((entry) => ({ kind: 'route', entry }));

    // Les entités n'apparaissent que sur saisie : la liste au repos reste
    // la navigation par pages.
    if (!q || !carriers?.length) return routeItems;
    const carrierItems: PaletteItem[] = carriers
      .filter((c) => normalize(`${c.name} ${c.code}`).includes(q))
      .slice(0, MAX_ENTITY_RESULTS)
      .map((carrier) => ({ kind: 'carrier', carrier }));
    return [...carrierItems, ...routeItems];
  }, [routes, carriers, query, t]);

  // Réinitialise l'état à chaque ouverture.
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlighted(0);
    }
  }, [open]);

  useEffect(() => setHighlighted(0), [query]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  function go(item: PaletteItem) {
    onClose();
    navigate(item.kind === 'route' ? item.entry.path : `/carriers/${item.carrier.id}`);
  }

  function onInputKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, results.length - 1));
      listRef.current?.children[Math.min(highlighted + 1, results.length - 1)]?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
      listRef.current?.children[Math.max(highlighted - 1, 0)]?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && results[highlighted]) {
      e.preventDefault();
      go(results[highlighted]);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center px-4 pt-[12vh]">
      <div className="absolute inset-0 bg-black/30 animate-fade-in" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.title')}
        tabIndex={-1}
        className="relative w-full max-w-lg overflow-hidden rounded border border-border bg-surface shadow-modal animate-scale-in outline-none"
      >
        <div className="flex items-center gap-2.5 border-b border-border px-4">
          <Search className="h-4 w-4 shrink-0 text-subtle" aria-hidden="true" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={canSearchCarriers ? t('palette.placeholderWithEntities') : t('palette.placeholder')}
            aria-label={t('palette.searchLabel')}
            role="combobox"
            aria-expanded="true"
            aria-controls="command-palette-results"
            aria-activedescendant={results[highlighted] ? `palette-opt-${highlighted}` : undefined}
            className="h-12 w-full bg-transparent text-sm text-text placeholder:text-subtle focus:outline-none"
          />
          <kbd className="hidden shrink-0 rounded border border-border bg-surface-subtle px-1.5 py-0.5 text-2xs text-subtle sm:block">
            Esc
          </kbd>
        </div>
        <ul
          ref={listRef}
          id="command-palette-results"
          role="listbox"
          aria-label={t('palette.resultsLabel')}
          className="max-h-[320px] overflow-y-auto scrollbar-thin p-1.5"
        >
          {results.length === 0 && (
            <li className="px-3 py-6 text-center text-[13px] text-subtle">{t('palette.noResults')}</li>
          )}
          {results.map((item, i) => {
            const Icon = item.kind === 'route' ? item.entry.icon : Truck;
            const label = item.kind === 'route' ? t(item.entry.labelKey) : item.carrier.name;
            return (
              <li
                key={itemKey(item)}
                id={`palette-opt-${i}`}
                role="option"
                aria-selected={i === highlighted}
                onMouseEnter={() => setHighlighted(i)}
                onMouseDown={(e) => {
                  // mousedown (et non click) : évite que le blur de l'input
                  // ferme/perturbe avant la navigation.
                  e.preventDefault();
                  go(item);
                }}
                className={cn(
                  'flex cursor-pointer items-center gap-2.5 rounded px-3 py-2 text-[13px]',
                  i === highlighted ? 'bg-accent-soft text-accent' : 'text-text',
                )}
              >
                {Icon ? (
                  <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden="true" />
                ) : (
                  <span className="h-4 w-4 shrink-0" aria-hidden="true" />
                )}
                <span className="flex-1 truncate">{label}</span>
                {item.kind === 'carrier' && (
                  <span className="shrink-0 rounded bg-surface-subtle px-1.5 py-0.5 text-2xs uppercase tracking-wider text-subtle">
                    {t('palette.carrierTag')}
                  </span>
                )}
                {i === highlighted && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden="true" />}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
